import { Devices } from "./Devices.js";
import { Mixer } from "./Mixer.js";
import { UserPrefs } from "./UserPrefs.js";
import { BrowserHandler } from "./BrowserHandler.js";
import { OutputConfig } from "./OutputConfig.js";
import { Peers_Handler } from "./Peers.js";
import { avail_devices, getSelectedDevice, setSelectedDevice, subscribeSelectedDeviceSelection } from "./stores.js";
import { get } from "svelte/store";

/*
 Output (speaker/stream) selection lives apart from Devices on purpose: output routing has no
 getUserMedia/permission dependency. It only needs the enumerated device list -- which Devices
 feeds into avail_devices whenever it (re)enumerates -- and the user's prefs. "stream" (eng /
 expanded-io talent) and "speaker" (client / talent) both name the one AudioContext sink
 depending on role; Mixer.setOutputDevice no-ops for the role-irrelevant key.

 Only roles with their own stream monitor menu (see streamIndependent) hold an independent
 "stream" selection. For everyone else "stream" is derived: it always mirrors the "speaker"
 selection (_reconcile), so conference audio (routed by "speaker") and the stream can never
 end up on different devices -- not even via a stream pref saved by an eng session on the
 same browser profile.
*/
export const OutputDevices = {

    self_type: "",

    // -- last sink id handed to Mixer per type; null until a first apply happens
    _applied: { speaker: null, stream: null },

    _unsub_avail: null,
    _unsub_mic: null,
    _unsub_speaker: null,
    _unsub_xio: null,

    
    _chain: Promise.resolve(),
    _enqueue(fn){
        const run = this._chain.then(fn);
        this._chain = run.catch(() => {});
        return run;
    },

    init(self_type){

        this.self_type = self_type;

        if (self_type === "eng")
            setSelectedDevice("stream", { muted: true });

        // -- subscribe to avail device changes
        this._unsub_avail && this._unsub_avail();
        this._unsub_avail = avail_devices.subscribe(() => this._queueReconcile());

        // -- anytime the mic changes we are notified and check wether we should change output device.
        // -- this can't hurt since we dedupe and may be required in cases where output is implied from mic selection. Like iOS or Safari
        this._unsub_mic && this._unsub_mic();
        this._unsub_mic = subscribeSelectedDeviceSelection("microphone", () => this._queueReconcile());

        // -- subscribe to speaker changes so that we can set the stream selection on client and talent peers
        this._unsub_speaker && this._unsub_speaker();
        this._unsub_speaker = subscribeSelectedDeviceSelection("speaker", () => this._queueReconcile());

        // -- expanded io flips whether a talent's stream is independent; re-mirror when it changes
        this._unsub_xio && this._unsub_xio();
        this._unsub_xio = Peers_Handler.self_peer?.use_expanded_io.subscribe(() => this._queueReconcile()) ?? null;
    },

    // -- is this eng or talent with plugin
    streamIndependent(){
        if (this.self_type === "eng")
            return true;
        return this.self_type === "talent"
            && !!Peers_Handler.self_peer && get(Peers_Handler.self_peer.use_expanded_io);
    },

    _queueReconcile(){
        this._enqueue(() => this._reconcile().catch(e => console.error(e)));
    },

    select(type, deviceId){

    		if (!this._supported())
            return Promise.resolve();
      
			// -- dont set a stream selection if we shouldn't
        if (type === "stream" && !this.streamIndependent())
            return Promise.resolve();

        const device = Devices.getDeviceByID(type, deviceId);
        return this._enqueue(() => this._apply(type, device?.label ?? "", deviceId));
    },

    _supported(){
        return !!OutputConfig.session?.browser
            && BrowserHandler.supports_setSpeakerId(OutputConfig.session.browser);
    },

    async _reconcile(){

        if (!this._supported())
            return;

        const devices = get(avail_devices);
        const speakers = devices?.speaker ?? [];
        if (speakers.length === 0)
            return; // -- nothing (usefully) enumerated yet

        for (const type of ["speaker", "stream"]){

            // -- just mirror the speaker selection (set before stream in this loop) if we are client or talent without plugin
            if (type === "stream" && !this.streamIndependent()){
                await this._mirrorSpeakerToStream();
                continue;
            }

            const selected = getSelectedDevice(type);

            // -- unset: first fill
            if (!selected?.label && !selected?.id){
                const device = this._firstFillDevice(type, devices, speakers);
                if (device)
                    await this._apply(type, device.label ?? "", device.deviceId ?? null);
                continue;
            }

            // -- if there is a legitimate selection, just re-apply it if we haven't already
            const still_present = (selected.id    && Devices.getDeviceByID(type, selected.id, devices))
                               || (selected.label && Devices.getDeviceByLabel(type, selected.label, devices));
            if (still_present){
            		// -- don't apply if we already did
                if (this._applied[type] === null)
                	await this._apply(type, selected.label ?? "", selected.id ?? null);
                continue;
            }

            const def = Devices.getDefaultDevice(speakers);
            if (!def)
                continue;

            console.warn(`[PL-Output] Selected ${type} output gone -- falling back to default:`, def.label);
            await this._apply(type, def.label ?? "", def.deviceId ?? null);
        }
    },

    async _apply(type, label, id){
        setSelectedDevice(type, { label: label, id: id });
        this._applied[type] = id ?? "default";
        await Mixer.setOutputDevice(type, label, this.self_type, id).catch(e => console.error(e));
    },

    async _mirrorSpeakerToStream(){

        const speaker = getSelectedDevice("speaker");
        if (!speaker?.label && !speaker?.id)
            return; // -- speaker hasn't filled yet; mirror once it does

        const stream = getSelectedDevice("stream");
        const same = (stream?.label ?? "") === (speaker.label ?? "")
                  && (stream?.id ?? null) === (speaker.id ?? null);

        if (!same || this._applied.stream === null)
            await this._apply("stream", speaker.label ?? "", speaker.id ?? null);
    },

    _firstFillDevice(type, devices, speakers){

        const pref = this._prefDevice(type, speakers);
        if (pref)
            return pref;

        if (type === "speaker"){
            const mic = getSelectedDevice("microphone");
            if (!mic?.label && !mic?.id)
                return null;
            return this._matchingSpeakerForMic(devices) ?? Devices.getDefaultDevice(speakers);
        }

        // -- independent roles only (a derived stream never reaches this fill): the stream
        // -- choice starts latched to the speaker choice and can then be changed separately
        return this._speakerSelectionAsDevice();
    },

    _prefDevice(type, speakers){

        const label = UserPrefs.prefs?.devices?.[type]?.label;
        if (!label)
            return null;

        return speakers.find(device => device.label === label) ?? null;
    },

    // -- the output that lives on the same physical device as the selected mic (matched by
    // -- groupId), e.g. a headset's speaker for a headset's mic
    _matchingSpeakerForMic(devices){

        const mic = getSelectedDevice("microphone");
        const mic_device = (devices.microphone ?? []).find(device => {
            return (mic?.id && device.deviceId === mic.id)
                || (mic?.label && device.label === mic.label);
        });

        if (!mic_device?.groupId)
            return null;

        return (devices.speaker ?? []).find(device => device.groupId === mic_device.groupId) ?? null;
    },

    // -- stream defaults to wherever the speaker selection landed (the loop fills speaker first)
    _speakerSelectionAsDevice(){
        const speaker = getSelectedDevice("speaker");
        if (!speaker?.label && !speaker?.id)
            return null;
        return { label: speaker.label ?? "", deviceId: speaker.id ?? null };
    },

}
